import React, { useEffect, useRef, useState, useCallback } from "react";
import { useGoogleMaps } from "../../../../hooks";

const loadScriptIfNeeded = (apiKey, onReady) => {
  if (window.google && window.google.maps) {
    onReady();
    return;
  }
  if (!apiKey) return;
  if (document.getElementById("__googleMapsScriptZones")) return;
  const js = document.createElement("script");
  js.id = "__googleMapsScriptZones";
  js.async = true;
  js.defer = true;
  js.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry`;
  js.onload = onReady;
  document.body.appendChild(js);
};

const BusinessZoneCircleMap = ({
  apiKey,
  center,
  initialRadiusKm = 1,
  value,
  onChange,
}) => {
  const { isLoaded } = useGoogleMaps();
  const ref = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const mapRef = useRef(null);
  const circleRef = useRef(null);
  const mapReadyRef = useRef(false);
  const onChangeRef = useRef(onChange);
  const initializedRef = useRef(false);
  const [hasDrawing, setHasDrawing] = useState(false);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!isLoaded) {
      loadScriptIfNeeded(apiKey, () => setMapReady(true));
    } else {
      setMapReady(true);
    }
  }, [isLoaded, apiKey]);

  // Handle circle center change
  const handleCircleCenterChanged = useCallback(() => {
    if (!circleRef.current) return;
    const circle = circleRef.current;
    if (onChangeRef.current) {
      onChangeRef.current({
        center: {
          lat: circle.getCenter().lat(),
          lng: circle.getCenter().lng(),
        },
        radio: circle.getRadius() / 1000,
      });
    }
  }, []);

  // Handle circle radius change
  const handleCircleRadiusChanged = useCallback(() => {
    if (!circleRef.current) return;
    const circle = circleRef.current;
    if (onChangeRef.current) {
      onChangeRef.current({
        center: {
          lat: circle.getCenter().lat(),
          lng: circle.getCenter().lng(),
        },
        radio: circle.getRadius() / 1000,
      });
    }
  }, []);

  const initializeCircle = useCallback(
    (map, initialValue) => {
      if (initializedRef.current) return;
      initializedRef.current = true;

      const initialCenter = initialValue?.center || {
        lat: center.lat,
        lng: center.lng,
      };
      const initialRadius = initialValue?.radio || initialRadiusKm;

      // Always use native Google Maps Circle for better editing experience
      const circle = new window.google.maps.Circle({
        center: initialCenter,
        radius: initialRadius * 1000, // Convert km to meters
        fillColor: "#2C7BE5",
        fillOpacity: 0.2,
        strokeColor: "#03459E",
        strokeWeight: 2,
        editable: true,
        draggable: true,
        map,
      });

      circleRef.current = circle;
      setHasDrawing(true);

      // Listen for changes
      window.google.maps.event.addListener(
        circle,
        "center_changed",
        handleCircleCenterChanged,
      );
      window.google.maps.event.addListener(
        circle,
        "radius_changed",
        handleCircleRadiusChanged,
      );

      // Fit bounds to circle
      map.fitBounds(circle.getBounds());

      // Emit initial data
      if (onChangeRef.current) {
        onChangeRef.current({
          center: initialCenter,
          radio: initialRadius,
        });
      }
    },
    [
      center,
      initialRadiusKm,
      handleCircleCenterChanged,
      handleCircleRadiusChanged,
    ],
  );

  // Clear the circle and reset to default
  const handleClear = useCallback(() => {
    if (circleRef.current) {
      circleRef.current.setMap(null);
      circleRef.current = null;
    }

    // Re-initialize with default values
    initializedRef.current = false;
    setHasDrawing(false);

    if (mapRef.current && window.google?.maps) {
      initializeCircle(mapRef.current, null);
    }
  }, [initializeCircle]);

  useEffect(() => {
    if (
      !mapReady ||
      !window.google?.maps ||
      !ref.current ||
      !center?.lat ||
      !center?.lng
    )
      return;
    if (mapReadyRef.current) return;

    const map = new window.google.maps.Map(ref.current, {
      center: { lat: center.lat, lng: center.lng },
      zoom: 12,
      streetViewControl: false,
      mapTypeControl: false,
    });
    mapRef.current = map;
    mapReadyRef.current = true;

    window.google.maps.event.addListenerOnce(map, "idle", () => {
      initializeCircle(map, value);
    });

    return () => {
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
      mapReadyRef.current = false;
      initializedRef.current = false;
    };
  }, [mapReady, center?.lat, center?.lng, initialRadiusKm]);

  useEffect(() => {
    if (
      mapRef.current &&
      mapReadyRef.current &&
      !initializedRef.current &&
      value
    ) {
      initializeCircle(mapRef.current, value);
    }
  }, [value, initializeCircle]);

  return (
    <div className="space-y-2">
      <div
        className="h-64 w-full overflow-hidden rounded-lg border border-gray-700 bg-gray-800 sm:h-80"
        ref={ref}
      />
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-400">
          Drag the circle to move it. Drag the edge to resize.
        </div>
        {hasDrawing && (
          <button
            type="button"
            onClick={handleClear}
            className="rounded border border-red-600/30 bg-red-600/20 px-3 py-1 text-xs text-red-400 transition-colors hover:bg-red-600/30 hover:text-red-300"
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
};

export default BusinessZoneCircleMap;
